import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import { keyBelongsElsewhere } from './guideTabTarget';
import styles from './RecordingStartGuide.module.css';

/**
 * The guide the web recorder's Start shows first (TOADO-969).
 *
 * The same mistake as on a page: pressing Start and talking. What is said
 * before the first ticket is started belongs to no ticket, so the guide says
 * how this recorder starts one before anything is shared. Nothing records
 * while it is open; "Got it, start recording" calls the same start the button
 * always did, inside its own click, so Chrome still sees the gesture it needs
 * to offer the screen picker.
 *
 * The words are this recorder's own. The page panel's guide talks about pins
 * and No pin ticket, which this recorder does not have.
 */

/** Whether this person chose not to see it, per signed-in user in this browser. */
const storageKey = (userId: string) => `toado.recordingGuideHidden.${userId}`;

function readHidden(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(storageKey(userId)) === 'true';
  } catch {
    // Unreadable storage shows the guide: once too often costs a click.
    return false;
  }
}

/** Remembered where it can be; kept for this page either way. */
export function useRecordingGuideHidden(userId: string | undefined): [boolean, (hidden: boolean) => void] {
  const [state, setState] = useState<{ userId: string | undefined; hidden: boolean }>(() => ({ userId, hidden: readHidden(userId) }));
  // Another person signed in: their own choice, never the previous one's.
  const current = state.userId === userId ? state : { userId, hidden: readHidden(userId) };
  if (current !== state) setState(current);
  const setHidden = useCallback((hidden: boolean) => {
    setState({ userId, hidden });
    if (!userId) return;
    try {
      if (hidden) window.localStorage.setItem(storageKey(userId), 'true');
      else window.localStorage.removeItem(storageKey(userId));
    } catch {
      // Not saved; this page still honours it.
    }
  }, [userId]);
  return [current.hidden, setHidden];
}

interface RecordingStartGuideProps {
  open: boolean;
  onCancel: () => void;
  /** Called from the click itself, so the start keeps its user gesture. */
  onStart: (dontShowAgain: boolean) => void;
}

export function RecordingStartGuide({ open, onCancel, onStart }: RecordingStartGuideProps) {
  const titleId = useId();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [learnMore, setLearnMore] = useState(false);
  const startRef = useRef<HTMLButtonElement>(null);
  const close = () => {
    setDontShowAgain(false);
    setLearnMore(false);
    onCancel();
  };
  /**
   * Tab stays among the guide's own four controls while it is open: it is a
   * modal step before a recording, and nothing behind it should start one.
   * Kept here rather than in the shared Modal, whose dialogs hold portalled
   * pickers and native disclosure controls a general trap would cut off.
   *
   * Only its own dialog's controls, and never while focus is in another
   * dialog or overlay: one opened over it (the command palette) keeps its own
   * Tab.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const dialog = startRef.current?.closest<HTMLElement>('[role="dialog"]');
      if (!dialog) return;
      if (keyBelongsElsewhere(dialog, event.composedPath(), document.activeElement)) return;
      const controls = [...dialog.querySelectorAll<HTMLElement>('[data-start-guide-control]')];
      if (controls.length === 0) return;
      const first = controls[0]!;
      const last = controls[controls.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && controls.includes(active);
      if (event.shiftKey && (!inside || active === first)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (!inside || active === last)) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <Modal open={open} onClose={close} width={520} labelledBy={titleId}>
      <ModalHeader><span id={titleId}>Start a ticket, then talk</span></ModalHeader>
      <ModalBody>
        <p className={styles.lead}>Each ticket begins when you start it. What you say after that goes into it.</p>
        <ol className={styles.steps}>
          <li>
            <strong>Start describing ticket</strong>
            <span>Press it and choose Bug or Feature request, or use Alt+S for a bug and Alt+R for a feature request. Then say what you see.</span>
          </li>
          <li>
            <strong>Capture moment</strong>
            <span>Alt+V keeps a frame of what is on screen for the ticket you are describing.</span>
          </li>
          <li>
            <strong>Finish ticket</strong>
            <span>Alt+X ends it. Start the next one the same way, or Stop when you are done.</span>
          </li>
        </ol>
        <p className={styles.note}>Anything you say before your first ticket stays in the recording, but is not added to a ticket.</p>
        <button type="button" className={styles.more} data-start-guide-control aria-expanded={learnMore} onClick={() => setLearnMore((value) => !value)}>
          {learnMore ? 'Less' : 'Learn more'}
        </button>
        {learnMore && (
          <ul className={styles.moreList}>
            <li>Something you add later in the walkthrough can sharpen an earlier ticket once the recording is processed.</li>
            <li>Toado files tickets on your board. It does not change your code or deploy anything.</li>
          </ul>
        )}
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <label className={styles.dontShow}>
          <input type="checkbox" data-start-guide-control checked={dontShowAgain} onChange={(event) => setDontShowAgain(event.target.checked)} />
          Don&apos;t show this again, next time just start the recording
        </label>
        <Button variant="secondary" data-start-guide-control onClick={close}>Cancel</Button>
        <Button
          ref={startRef}
          variant="primary"
          size="lg"
          data-start-guide-control
          // Where the Modal puts focus as it opens: one placement, no second timer.
          data-autofocus
          onClick={() => {
            const choice = dontShowAgain;
            setDontShowAgain(false);
            setLearnMore(false);
            onStart(choice);
          }}
        >
          Got it, start recording
        </Button>
      </ModalFooter>
    </Modal>
  );
}
